// ============================================
// EMPLOYEE TRACKER - PROFESSIONAL SAAS VERSION
// ============================================

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
GoogleMap,
Marker,
Polyline,
Circle,
InfoWindow,
OverlayView,
useJsApiLoader
} from "@react-google-maps/api";

import {
Users,
ShieldAlert,
Building2,
Smartphone,
Activity,
MapPin,
Clock3
} from "lucide-react";

const GOOGLE_MAP_KEY = "AIzaSyAmGcPTfePx1WqX-8bqlPno6-5hU_GE9uAY";

const mapContainerStyle = {
width: "100%",
height: "100%"
};

const buildingMapStyle = [
  {
    featureType: "poi.business",
    elementType: "all",
    stylers: [{ visibility: "on" }]
  },
  {
    featureType: "poi.park",
    elementType: "all",
    stylers: [{ visibility: "on" }]
  },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ color: "#e5e7eb" }]
  },
  {
    featureType: "landscape",
    stylers: [{ color: "#f8fafc" }]
  }
];

const colors = {
  primary: "#dd3537",     // Sivaji Brand Crimson
  background: "#fdf9f7",  // Peach-tint Background
  card: "#ffffff",        // Pure White Cards
  border: "#f0ecea",      // Soft Peach Border
  text: "#222222",        // Deep Grey Text
  muted: "#888888",
  success: "#28a745",
  danger: "#dc3545"
};

const EmployeeTracker = () => {
const [employees, setEmployees] = useState([]);
const [departments, setDepartments] = useState([]);
const [factory, setFactory] = useState(null);
const [alerts, setAlerts] = useState([]);
const [allPaths, setAllPaths] = useState([]);
const [loadingTrace, setLoadingTrace] = useState(false);
const [selectedDept, setSelectedDept] = useState("ALL");
const [selectedEmp, setSelectedEmp] = useState("ALL");
const today = new Date().toISOString().split("T")[0];

const [selectedDate, setSelectedDate] = useState(today);
const [selectedMarker, setSelectedMarker] = useState(null);


// Add this inside the EmployeeTracker function
const [windowWidth, setWindowWidth] = useState(window.innerWidth);

useEffect(() => {
  const handleResize = () => setWindowWidth(window.innerWidth);
  window.addEventListener('resize', handleResize);
  return () => window.removeEventListener('resize', handleResize);
}, []);



const [mapCenter, setMapCenter] = useState({
lat: 17.44297201,
lng: 78.47992000
});

const { isLoaded } = useJsApiLoader({
  id: 'google-map-script', // Must match Sessions.js exactly
  googleMapsApiKey: "AIzaSyAmGcPTfePx1WqX-8bqlPno6-5hU_GE9uA" // Must match Sessions.js exactly
});

// ============================================
// INITIAL LOAD
// ============================================

useEffect(() => {
loadDashboard();
}, []);

// ============================================
// AUTO FILTERING
// ============================================
useEffect(() => {
  setAllPaths([]); // 👈 THIS removes ghost lines immediately

  if (selectedEmp !== "ALL") {
    loadEmployeeTrace(selectedEmp);
  } else {
    loadAllPaths(selectedDept);
  }
}, [selectedDept, selectedEmp, selectedDate]);
// ============================================
// FETCH ALL DATA
// ============================================

const loadDashboard = async () => {
try {
const [
empRes,
depRes,
factoryRes,
alertsRes,
pathRes
] = await Promise.all([
axios.get("https://gpsapi.emedha.in/app/api/tracker/live-status"),
axios.get("https://gpsapi.emedha.in/app/api/department"),
axios.get("https://gpsapi.emedha.in/app/api/factory"),
axios.get("https://gpsapi.emedha.in/app/api/tracker/alerts"),
axios.get("https://gpsapi.emedha.in/app/api/tracker/all-paths")
]);


  setEmployees(empRes.data);
  setDepartments(depRes.data);
  setFactory(factoryRes.data);
  setAlerts(alertsRes.data);
  setAllPaths(pathRes.data);

} catch (err) {
  console.error(err);
}


};



useEffect(() => {
  const dept = departments.find(d => d.id === selectedDept);

  if (dept && selectedDept !== "ALL") {
    setMapCenter({
      lat: parseFloat(dept.latitude),
      lng: parseFloat(dept.longitude)
    });
  }
}, [selectedDept]);

// ============================================
// LOAD ALL PATHS
// ============================================

const loadAllPaths = async (department = "ALL") => {
  try {
    const res = await axios.get(
      `https://gpsapi.emedha.in/app/api/tracker/all-paths`,
      {
        params: {
          department,
          date: selectedDate
        }
      }
    );

    // ❗ IMPORTANT: if empty → CLEAR MAP
    if (!res.data || res.data.length === 0) {
      setAllPaths([]);
      return;
    }

    setAllPaths(res.data);

  } catch (err) {
    console.error(err);
    setAllPaths([]);
  }
};
// ============================================
// LOAD EMPLOYEE TRACE
// ============================================

const loadEmployeeTrace = async (employeeId) => {
  setLoadingTrace(true);

  try {
    const res = await axios.get(
      `https://gpsapi.emedha.in/app/api/tracker/path/${employeeId}`,
      {
        params: {
          date: selectedDate
        }
      }
    );

    const validPoints = (res.data.points || []).filter(
      (p) =>
        p &&
        !isNaN(p.lat) &&
        !isNaN(p.lng)
    );

    // ❗ IMPORTANT: CLEAR OLD DATA FIRST
    if (validPoints.length === 0) {
      setAllPaths([]);   // <-- THIS FIXES GHOST LINES
      return;
    }

    setAllPaths([
      {
        employee_id: res.data.employee_id,
        employee_name: res.data.employee_name,
        points: validPoints
      }
    ]);

    setMapCenter(validPoints[validPoints.length - 1]);

  } catch (err) {
    console.error(err);
    setAllPaths([]); // safety reset
  } finally {
    setLoadingTrace(false);
  }
};


// ============================================
// KPI
// ============================================

const activeEmployees = useMemo(() => {
return employees.filter(
(e) => e.session_status === "ACTIVE"
).length;
}, [employees]);

// ============================================
// RENDER
// ============================================




if (!isLoaded || !window.google) {
  return (
    <div style={{
      height: '100vh',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      background: '#020617',
      color: '#fff',
      fontSize: 18,
      fontWeight: 600
    }}>
      Loading Employee Tracker...
    </div>
  );
}


const formatTime = (dateString) => {
  return new Date(dateString).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true
  });
};




return ( <div style={styles.container}>


  {/* ====================================== */}
  {/* HEADER */}
  {/* ====================================== */}

  <div style={styles.header}>

    <div>
      <div style={styles.title}>
        Employees Tracker
      </div>

      <div style={styles.subtitle}>
        Enterprise Workforce Intelligence Platform
      </div>
    </div>

    <div style={styles.filters}>

        <input
type="date"
value={selectedDate}
onChange={(e) => setSelectedDate(e.target.value)}
style={styles.dateInput}
/>

      <select
        style={styles.select}
        value={selectedDept}
        onChange={(e) => {
          setSelectedDept(e.target.value);
          setSelectedEmp("ALL");
        }}
      >
        <option value="ALL">All Departments</option>

        {departments.map((dep) => (
          <option key={dep.id} value={dep.id}>
            {dep.name}
          </option>
        ))}
      </select>

      <select
        style={styles.select}
        value={selectedEmp}
        onChange={(e) => setSelectedEmp(e.target.value)}
      >
        <option value="ALL">All Employees</option>

        {employees
          .filter(
            (e) =>
              selectedDept === "ALL" ||
              e.assigned_department_id === selectedDept
          )
          .map((emp) => (
            <option key={emp.id} value={emp.id}>
              {emp.name}
            </option>
          ))}
      </select>

    </div>
  </div>

  {/* ====================================== */}
  {/* KPI */}
  {/* ====================================== */}

  {/* <div style={styles.kpiGrid}>

    <div style={styles.kpiCard}>
      <Users color="#60a5fa" />
      <div>
        <div style={styles.kpiValue}>
          {employees.length}
        </div>
        <div style={styles.kpiLabel}>
          Employees
        </div>
      </div>
    </div>

    <div style={styles.kpiCard}>
      <Activity color="#10b981" />
      <div>
        <div style={styles.kpiValue}>
          {activeEmployees}
        </div>
        <div style={styles.kpiLabel}>
          Live Sessions
        </div>
      </div>
    </div>

    <div style={styles.kpiCard}>
      <Building2 color="#a78bfa" />
      <div>
        <div style={styles.kpiValue}>
          {departments.length}
        </div>
        <div style={styles.kpiLabel}>
          Departments
        </div>
      </div>
    </div>

    <div style={styles.kpiCard}>
      <ShieldAlert color="#f59e0b" />
      <div>
        <div style={styles.kpiValue}>
          {alerts.length}
        </div>
        <div style={styles.kpiLabel}>
          Alerts
        </div>
      </div>
    </div>

  </div> */}

  {/* ====================================== */}
  {/* MAIN */}
  {/* ====================================== */}

  <div style={styles.main}>

    {/* ================================== */}
    {/* SIDEBAR */}
    {/* ================================== */}

    <div style={styles.sidebar}>

      {employees
        .filter(
          (e) =>
            selectedDept === "ALL" ||
            e.assigned_department_id === selectedDept
        )
        .map((emp) => (

          <div
            key={emp.id}
            style={{
              ...styles.employeeCard,
              border:
                selectedEmp == emp.id
                  ? "1px solid #2563eb"
                  : "1px solid #1e293b"
            }}
            onClick={() => setSelectedEmp(emp.id)}
          >

            <div style={styles.avatar}>
              {emp.name?.charAt(0)}
            </div>

            <div style={{ flex: 1 }}>

              <div style={styles.empName}>
                {emp.name}
              </div>

              <div style={styles.empDept}>
                {emp.dept_name}
              </div>

            
            </div>

          </div>
        ))}

    </div>

    {/* ================================== */}
    {/* MAP */}
    {/* ================================== */}

    <div style={styles.mapContainer}>

      {isLoaded && (

        <GoogleMap
          key={selectedDate + selectedEmp + selectedDept}
          mapContainerStyle={mapContainerStyle}
          center={mapCenter}
            mapTypeId="hybrid"
          zoom={18}
          options={{
            styles: buildingMapStyle,
            disableDefaultUI: true,
            zoomControl: true
          }}
        >

          {/* ================================= */}
          {/* FACTORY BOUNDARY */}
          {/* ================================= */}

{factory && (
  <>
    <Circle
      center={{
        lat: +factory.latitude,
        lng: +factory.longitude
      }}
      radius={+factory.radius}
      options={{
        fillColor: "#dd3537",
        fillOpacity: 0.08,
        strokeColor: "#dd3537",
        strokeWeight: 2
      }}
    />
  </>
)}

          {/* ================================= */}
          {/* DEPARTMENT CIRCLES */}
          {/* ================================= */}

          {departments.map((dep) => (

            <Circle
              key={dep.id}
              center={{
                lat: parseFloat(dep.latitude),
                lng: parseFloat(dep.longitude)
              }}
              radius={parseFloat(dep.radius)}
              options={{
                fillColor:
                  selectedDept === dep.id
                    ? "#10b981"
                    : "#6366f1",

                fillOpacity: 0.15,

                strokeColor:
                  selectedDept === dep.id
                    ? "#10b981"
                    : "#6366f1",

                strokeWeight: 2
              }}
            />

          ))}

     {/* ================================= */}
{/* EMPLOYEE PATHS */}
{/* ================================= */}
{allPaths
  .filter(p => p?.points && p.points.length > 0)
  .map((empPath) => {
    if (!empPath?.points?.length) return null;

    const points = empPath.points.filter(
      p => p && !isNaN(p.lat) && !isNaN(p.lng)
    );

    if (points.length === 0) return null;

    const latestPoint = points[points.length - 1];

    return (
      <React.Fragment key={empPath.employee_id}>

        {/* ============================== */}
        {/* PATH LINE */}
        {/* ============================== */}

        {empPath.points.length > 1 && (
    <Polyline
  path={points}
  options={{
    strokeColor: "#3b82f6",
    strokeOpacity: 0.8,
    strokeWeight: 3,
    icons: [
      {
        icon: {
          path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
          scale: 3
        },
        offset: "100%",
        repeat: "80px"
      }
    ]
  }}
/>
        )}

        {/* ============================== */}
        {/* START POINT */}
        {/* ============================== */}

        <Marker
          position={empPath.points[0]}
          icon={{
            path: window.google.maps.SymbolPath.CIRCLE,
            fillColor: "#22c55e",
            fillOpacity: 1,
            strokeColor: "#fff",
            strokeWeight: 3,
            scale: 6
          }}
        />

        {/* ============================== */}
        {/* LIVE EMPLOYEE MARKER */}
        {/* ============================== */}

      {/* ============================== */}
{/* LIVE EMPLOYEE MARKER */}
{/* ============================== */}

<Marker
  position={latestPoint}
  onClick={() =>
    setSelectedMarker({
      employee: empPath.employee_name,
      point: latestPoint
    })
  }
  icon={{
    url:
      "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
    scaledSize: new window.google.maps.Size(42, 42)
  }}
/>

{/* EMPLOYEE NAME TAG */}

<OverlayView
  position={latestPoint}
  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
>
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 6,

      background: "#111827",
      color: "#ffffff",

      padding: "8px 14px",

      borderRadius: 12,

      fontSize: 13,
      fontWeight: 700,

      minWidth: "max-content",

      whiteSpace: "nowrap",

      boxShadow: "0 4px 12px rgba(0,0,0,0.35)",

      border: "2px solid #ffffff",

      transform: "translate(-50%, -100px)"
    }}
  >
    <div
      style={{
        width: 22,
        height: 22,
        borderRadius: "50%",
        background: "#ef4444",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 12
      }}
    >
      👤
    </div>

    <span>
      {empPath.employee_name}
    </span>
  </div>
</OverlayView>
        {/* ============================== */}
        {/* ALL POINTS */}
        {/* ============================== */}

 {/* ============================== */}
{/* MOVEMENT POINTS */}
{/* ============================== */}

{points.map((point, idx) => {

  if (
    !point ||
    isNaN(point.lat) ||
    isNaN(point.lng)
  ) {
    return null;
  }

  return (
    <React.Fragment key={idx}>

      {/* MOVEMENT DOT */}

      <Marker
        position={point}

        icon={{
          path: window.google.maps.SymbolPath.CIRCLE,

          fillColor:
            idx === points.length - 1
              ? "#22c55e"
              : "#3b82f6",

          fillOpacity: 1,

          strokeColor: "#ffffff",

          strokeWeight: 2,

          scale:
            idx === points.length - 1
              ? 8
              : 5
        }}

        onClick={() =>
          setSelectedMarker({
            employee: empPath.employee_name,
            point,
              movement: idx + 1,
    time: point.time
          })
        }
      />



    </React.Fragment>
  );

})}

      </React.Fragment>
    );
  })}




{/* ================================= */}
{/* ALERTS */}
{/* ================================= */}

{alerts
  .filter(
    (alert) =>
      alert.latitude &&
      alert.longitude
  )
  .map((alert) => (

    <Marker
      key={alert.id}

      position={{
        lat: parseFloat(alert.latitude),
        lng: parseFloat(alert.longitude)
      }}

      icon={{
        path:
          window.google.maps.SymbolPath.BACKWARD_CLOSED_ARROW,

        fillColor: "#ef4444",
        fillOpacity: 1,

        strokeColor: "#fff",
        strokeWeight: 2,

        scale: 6
      }}
    />

))}
          {/* ================================= */}
          {/* INFO WINDOW */}
          {/* ================================= */}

        {selectedMarker && (
  <InfoWindow
    position={selectedMarker.point}
    onCloseClick={() => setSelectedMarker(null)}
  >
    <div style={{
      minWidth: 180,
      padding: 10,
      fontFamily: "sans-serif"
    }}>
      
      <div style={{
        fontWeight: 800,
        fontSize: 14,
        marginBottom: 6
      }}>
        {selectedMarker.employee}
      </div>

      <div style={{
        fontSize: 12,
        color: "#374151",
        marginBottom: 4
      }}>
        Movement: #{selectedMarker.movement}
      </div>

      <div style={{
        fontSize: 12,
        color: "#10b981",
        fontWeight: 700
      }}>
        ⏱ {selectedMarker.time ? formatTime(selectedMarker.time) : "N/A"}
      </div>

    </div>
  </InfoWindow>
)}

        </GoogleMap>

      )}

    </div>
  </div>
</div>


);
};

export default EmployeeTracker;

// ============================================
// STYLES
// ============================================

const styles = {

  container: {
    height: "100vh",
    background: colors.background, // Peach Background
    display: "flex",
    flexDirection: "column",
    color: colors.text
  },
  header: {
    padding: "20px 30px",
    background: "#fff",
    borderBottom: `1px solid ${colors.border}`,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },
  select: {
    background: "#fff",
    border: `1px solid ${colors.border}`,
    color: colors.text,
    padding: "12px 18px",
    borderRadius: 12,
    outline: "none",
    minWidth: 220,
    fontWeight: "600"
  },
  kpiCard: {
    background: "#fff",
    border: `1px solid ${colors.border}`,
    borderRadius: 20,
    padding: 20,
    display: "flex",
    alignItems: "center",
    gap: 20,
    boxShadow: "0 4px 12px rgba(0,0,0,0.03)"
  },
  sidebar: {
    width: 340,
    background: colors.background,
    borderRight: `1px solid ${colors.border}`,
    overflowY: "auto",
    padding: 15
  },
  employeeCard: {
    background: "#fff",
    borderRadius: 20,
    padding: 16,
    marginBottom: 12,
    display: "flex",
    gap: 15,
    cursor: "pointer",
    transition: "0.3s",
    boxShadow: "0 2px 8px rgba(0,0,0,0.02)"
  },
  avatar: {
    width: 52,
    height: 52,
    borderRadius: 18,
    background: colors.primary, // Crimson Avatar
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 800,
    fontSize: 20
  },


title: {
fontSize: 28,
fontWeight: 800
},

subtitle: {
color: "#94a3b8",
marginTop: 5
},

filters: {
display: "flex",
gap: 15
},



kpiGrid: {
display: "grid",
gridTemplateColumns: "repeat(4,1fr)",
gap: 20,
padding: 20
},


kpiValue: {
fontSize: 24,
fontWeight: 800
},

kpiLabel: {
color: "#94a3b8"
},

main: {
flex: 1,
display: "flex",
overflow: "hidden"
},







empName: {
fontWeight: 700,
fontSize: 16
},

empDept: {
color: "#94a3b8",
marginTop: 4,
fontSize: 13
},

liveRow: {
display: "flex",
alignItems: "center",
gap: 8,
marginTop: 10,
fontSize: 11,
color: "#10b981",
fontWeight: 700
},

liveDot: {
width: 8,
height: 8,
borderRadius: 999,
background: "#10b981"
},

mapContainer: {
flex: 1
},
// ============================================
// ADD THIS STYLE
// ============================================

dateInput: {
background: "#fff",
border: `1px solid ${colors.border}`,
color: colors.text,
padding: "12px 18px",
borderRadius: 12,
outline: "none",
minWidth: 180,
fontWeight: "600"
},
header: {
    padding: "20px 30px",
    background: "#fff",
    borderBottom: `1px solid ${colors.border}`,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap", // 👈 Allows filters to drop to next line on mobile
    gap: "15px"
  },

  filters: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap", // 👈 Selects will wrap on narrow screens
    width: window.innerWidth < 768 ? "100%" : "auto"
  },

  main: {
    flex: 1,
    display: "flex",
    flexDirection: window.innerWidth < 768 ? "column-reverse" : "row", // 👈 Map on top, sidebar below on mobile
    overflow: "hidden"
  },

  sidebar: {
    width: window.innerWidth < 768 ? "100%" : "340px", // 👈 Full width on mobile
    height: window.innerWidth < 768 ? "40vh" : "auto", // 👈 Limit height on mobile so map is visible
    background: colors.background,
    borderRight: window.innerWidth < 768 ? "none" : `1px solid ${colors.border}`,
    borderTop: window.innerWidth < 768 ? `1px solid ${colors.border}` : "none",
    overflowY: "auto",
    padding: 15
  },

  mapContainer: {
    flex: 1,
    height: window.innerWidth < 768 ? "60vh" : "100%", // 👈 Larger map area for mobile
    width: "100%"
  },

  select: {
    background: "#fff",
    border: `1px solid ${colors.border}`,
    color: colors.text,
    padding: "10px 14px",
    borderRadius: 10,
    outline: "none",
    flex: window.innerWidth < 768 ? "1" : "none", // 👈 Grow to fill space on mobile
    minWidth: window.innerWidth < 768 ? "140px" : "200px",
    fontSize: "13px"
  }

};
